import { Badge } from '@/components/badge'
import { Button } from '@/components/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/form'
import { Input } from '@/components/input'
import { ToggleGroup, ToggleGroupItem } from '@/components/toggle-group'
import { DataTable } from '@frontend/components/enhanced/data-table'
import { RawLaptopData } from '@frontend/core'
import {
  useDeleteLaptopFromOrgMutation,
  useUpdateLaptopRoleMutation
} from '@frontend/core/lib/domains/laptops'
import { toast } from '@frontend/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { ColumnDef } from '@tanstack/react-table'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useLaptopsData } from './hooks'

export function LaptopTable({
  filteredData,
  isLoading,
  filters,
  updateFilterValue,
  showFilters
}: ReturnType<typeof useLaptopsData> & {
  onLaptopClick?: (teamId: string) => void
  showFilters?: (keyof ReturnType<typeof useLaptopsData>['filters'])[]
}) {
  const [deleteLaptop, deleteResult] = useDeleteLaptopFromOrgMutation()

  const columns: ColumnDef<RawLaptopData>[] = [
    {
      id: 'logo',
      header: '',
      cell: ({ row }) => {
        const team = row.original
        return (
          <div className='flex items-center justify-center gap-2'>
            {/*{team.logo && <image src={arsenal} alt={team.name} className='h-7' />}*/}
          </div>
        )
      }
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const laptop = row.original
        return <div className='flex items-center gap-2'>{laptop.name}</div>
      }
    },
    {
      id: 'role',
      header: 'Role',
      cell: ({ row }) => (
        <div className='w-32'>
          <Badge variant='outline' className='text-muted-foreground px-1.5'>
            {row.original.role}
          </Badge>
        </div>
      )
    }
  ]

  return (
    <DataTable
      name='Laptop'
      columns={columns}
      data={filteredData}
      dataForm={LaptopForm}
      isLoading={isLoading}
      additionalActions={[
        {
          label: 'Delete',
          onClick: (row: RawLaptopData) => {
            deleteLaptop({
              id: row.id,
              name: row.name
            })
          }
        }
      ]}
      enableCreate={false}
      defaultSorting={[{ id: 'name', desc: false }]}
      filters={filters}
      updateFilterValue={updateFilterValue}
      showFilters={showFilters ?? []}
    />
  )
}

const formSchema = z.object({
  id: z.string().optional(),
  name: z.string(),
  role: z.enum(['NoAccess', 'Viewer'])
})

export function LaptopForm({
  onSubmit,
  initialValues
}: {
  onSubmit: (values: z.infer<typeof formSchema>) => void
  initialValues?: z.infer<typeof formSchema>
}) {
  const [updateLaptop, updateResult] = useUpdateLaptopRoleMutation()
  // const [createLaptop, createResult] = useCreateLaptopMutation()

  const handleSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      if (values.id) {
        toast.promise(
          updateLaptop({
            name: values.name,
            role: values.role
          }).unwrap(),
          {
            loading: 'Updating...',
            success: (data: RawLaptopData) => {
              onSubmit(values)
              return `${data.name} has been updated`
            },
            error: 'Error'
          }
        )
      }
    } catch (err) {
      console.error('Update failed:', err)
    }
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialValues?.name,
      role: initialValues?.role
    },
    values: initialValues
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-8'>
        <FormField
          control={form.control}
          name='name'
          defaultValue={initialValues?.name}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Laptop Name</FormLabel>
              <FormControl>
                <Input placeholder='' disabled={true} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='role'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role</FormLabel>
              <FormControl>
                <ToggleGroup
                  type='single'
                  {...field}
                  onValueChange={(val) => field.onChange(val)}
                >
                  {formSchema.shape.role.options.map((option) => (
                    <ToggleGroupItem
                      key={option}
                      value={option}
                      className='data-[state=on]:bg-primary data-[state=on]:text-primary-foreground min-w-auto border'
                    >
                      {option}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className='flex justify-end'>
          <Button className='' type='submit'>
            {updateResult.isLoading ? 'Updating...' : 'Update'}
          </Button>
        </div>
      </form>
    </Form>
  )
}
